import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Film, Target, ExternalLink } from 'lucide-react';

export const OutOfBounds = () => {
  return (
    <div className="pt-20" data-testid="out-of-bounds-page">
      {/* Header */}
      <section className="py-12 bg-zinc-50 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <Link 
            to="/newsroom" 
            className="inline-flex items-center text-[#5B4FF9] hover:text-[#4A3FE8] font-medium mb-6 transition-colors"
            data-testid="back-to-newsroom"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Newsroom
          </Link>
          
          <div className="flex items-center gap-4 text-sm text-zinc-500 mb-4">
            <span className="bg-[#5B4FF9] text-white px-3 py-1 rounded-full text-xs font-semibold">
              Press Release
            </span>
            <span className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              September 21, 2026
            </span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight font-extrabold text-[#272926] leading-tight">
            Players Health Foundation Launches Capital Campaign for "Out of Bounds," a Feature Documentary on the Fight to Reclaim Youth Sports
          </h1>
        </div>
      </section>

      {/* Content */}
      <article className="py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {/* Lead */}
          <p className="text-xl text-[#5B4FF9] font-semibold mb-8 leading-relaxed">
            $750,000 campaign to fund "Out of Bounds: Reclaiming the Game" — a feature documentary on the youth sports boom, the harm it has left behind, and the movement working to give the game back to kids.
          </p>

          {/* Body */}
          <div className="prose prose-lg max-w-none text-zinc-700 space-y-6">
            <p>
              <strong>September 21, 2026</strong> — The Players Health Foundation today announced the public launch of a $750,000 capital campaign to fund "Out of Bounds: Reclaiming the Game," a feature documentary on the youth sports boom, the harm it has left behind, and the movement working to give the game back to kids.
            </p>

            <p>
              The campaign supports the film and the Foundation's broader mission across three pillars: access, safety, and the game of sport itself. Proceeds beyond the production budget will extend the Foundation's grantmaking, including the programs that serve its first grantee class of 20 organizations this fall.
            </p>

            {/* Film Info Box */}
            <div className="bg-[#272926] rounded-2xl p-8 my-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <Film className="h-8 w-8 text-[#5B4FF9]" />
                <h3 className="text-xl font-bold">About the Film</h3>
              </div>
              <p className="text-zinc-300 mb-4">
                "Out of Bounds" is structured in three acts: the boom that turned youth sports into an industry, the harm that industry has produced, and the people working to reclaim it.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-zinc-400">Director</p>
                  <p className="font-semibold">Gerard Roxburgh</p>
                  <p className="text-zinc-400 text-xs">Rox Enterprises</p>
                </div>
                <div>
                  <p className="text-zinc-400">Producer</p>
                  <p className="font-semibold">John Guydon</p>
                </div>
                <div>
                  <p className="text-zinc-400">Executive Producer</p>
                  <p className="font-semibold">Devin Johnson</p>
                  <p className="text-zinc-400 text-xs">Netflix, FX, HBO</p>
                </div>
              </div>
              <p className="text-[#5B4FF9] font-semibold mt-4">Premiere targeted for 2027</p>
            </div>

            <p>
              The urgency is measurable. Roughly half of overuse injuries in youth athletes are preventable. Injury claims across the youth sports economy are rising roughly four times faster than the revenue that economy generates. The gap between what the sector earns and what it protects continues to widen.
            </p>

            <p>
              The campaign enters its public phase with a $100,000 lead commitment from a family foundation and roughly a third of the goal already committed. Charitable gifts are made through the Players Health Foundation, a 501(c)(3) organization, with corporate sponsorship opportunities available separately at the presenting, sponsor, and partner levels.
            </p>

            {/* Quote */}
            <blockquote className="border-l-4 border-[#5B4FF9] pl-6 py-4 my-8 bg-[#F5F4FF] rounded-r-xl">
              <p className="text-lg italic text-[#272926] mb-4">
                "This project is personal to me. It's about the kids carrying risk that adults created, and it's about the families who deserve better than that. I believe we can give the game back to the kids it belongs to, but only if we tell the truth about what it's become. This film is how we tell that truth, and this campaign is how we reclaim the game our kids love."
              </p>
              <footer className="text-sm font-semibold text-[#5B4FF9]">
                — Tyrre Burks, Founder and CEO of Players Health and President of the Players Health Foundation Board
              </footer>
            </blockquote>

            {/* Campaign Goals */}
            <div className="bg-[#5B4FF9] rounded-2xl p-8 my-8">
              <div className="flex items-center gap-3 mb-6">
                <Target className="h-8 w-8 text-white" />
                <h3 className="text-xl font-bold text-white">Campaign Goals</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-4xl font-extrabold text-white mb-2">10M+</p>
                  <p className="text-white/80 text-sm">Viewers reached</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-extrabold text-white mb-2">1,000+</p>
                  <p className="text-white/80 text-sm">Young athletes funded in year one</p>
                </div>
                <div className="text-center">
                  <p className="text-4xl font-extrabold text-white mb-2">3-5</p>
                  <p className="text-white/80 text-sm">States with safety legislation support</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="bg-zinc-100 rounded-2xl p-8 my-10 text-center">
              <h3 className="text-xl font-bold text-[#272926] mb-4">Support the Campaign</h3>
              <p className="text-zinc-600 mb-6">Learn more about the film and view the campaign materials, or contact the Foundation to discuss how you can help.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://phfoutofbounds.netlify.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#5B4FF9] text-white rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
                  data-testid="campaign-site-btn"
                >
                  View Campaign Site
                  <ExternalLink className="ml-2 h-4 w-4" />
                </a>
                <a
                  href="mailto:kyle.lubrano@playershealth.com"
                  className="bg-white text-[#5B4FF9] border-2 border-[#5B4FF9] rounded-full px-8 py-3 font-semibold transition-all transform hover:-translate-y-1 shadow-sm hover:shadow-lg inline-flex items-center justify-center"
                  data-testid="contact-btn"
                >
                  Contact Foundation
                </a>
              </div>
            </div>

            {/* About Sections */}
            <div className="border-t border-zinc-200 pt-10 mt-10">
              <h3 className="text-lg font-bold text-[#272926] mb-4">About Players Health Foundation</h3>
              <p className="text-zinc-600 mb-8">
                The Players Health Foundation is a 501(c)(3) organization working to make sport safer and more accessible for every young athlete. Its work spans three pillars: access, so that cost is not the reason a child stops playing; safety, so that preventable harm is prevented; and the game of sport, so that the culture around youth athletics serves the athletes in it. The Foundation awarded its first class of 20 grantees in 2026.
              </p>

              <h3 className="text-lg font-bold text-[#272926] mb-4">About Players Health</h3>
              <p className="text-zinc-600 mb-8">
                Players Health is a sports insurance and athlete safety technology company serving youth sports and fitness organizations across the United States. The company pairs insurance coverage with safety programming, abuse prevention training, and risk data, working from the conviction that safer organizations should be measurably better organizations to insure.
              </p>

              {/* Media Contact */}
              <div className="bg-zinc-100 rounded-xl p-6">
                <h4 className="text-sm font-bold text-zinc-500 uppercase tracking-wider mb-3">Media Contact</h4>
                <p className="text-[#272926] font-semibold">Kyle Lubrano</p>
                <p className="text-zinc-600 text-sm mb-2">Vice President, Players Health Foundation</p>
                <a href="mailto:kyle.lubrano@playershealth.com" className="text-[#5B4FF9] hover:underline">
                  kyle.lubrano@playershealth.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
};
